import { Inject, Injectable, Optional, InjectionToken } from '@angular/core';
import { DatetimeAdapter, MTX_DATETIME_FORMATS, MtxDatetimeFormats } from '@ng-matero/extensions/core';
import { DateTime, Info, Settings } from 'luxon';

export interface MtxLuxonDateAdapterOptions {
  useUtc?: boolean;
  firstDayOfWeek?: number;
}

export const MTX_LUXON_DATE_ADAPTER_OPTIONS = new InjectionToken<MtxLuxonDateAdapterOptions>(
  'MTX_LUXON_DATE_ADAPTER_OPTIONS'
);

@Injectable()
export class MtxLuxonDatetimeAdapter extends DatetimeAdapter<DateTime> {
  private _useUtc = false;
  private _firstDayOfWeek = 1; // Monday for Dutch locale

  constructor(
    @Optional() @Inject(MTX_LUXON_DATE_ADAPTER_OPTIONS) options?: MtxLuxonDateAdapterOptions
  ) {
    super();
    this._useUtc = !!options?.useUtc;
    this._firstDayOfWeek = options?.firstDayOfWeek ?? 1;
  }

  override setLocale(locale: string): void {
    super.setLocale(locale);
    Settings.defaultLocale = locale;
  }

  override getYear(date: DateTime): number {
    return date.year;
  }

  override getMonth(date: DateTime): number {
    return date.month - 1; // Luxon months are 1-indexed, Angular Material expects 0-indexed
  }

  override getDate(date: DateTime): number {
    return date.day;
  }

  getHour(date: DateTime): number {
    return date.hour;
  }

  getMinute(date: DateTime): number {
    return date.minute;
  }

  getFirstDateOfMonth(date: DateTime): DateTime {
    return date.startOf('month');
  }

  getWeek(date: DateTime, _firstDayOfWeek: number): number {
    return date.weekNumber;
  }

  isInNextMonth(startDate: DateTime, endDate: DateTime): boolean {
    const startMonth = startDate.month;
    const endMonth = endDate.month;
    const startYear = startDate.year;
    const endYear = endDate.year;

    return (endYear > startYear) || (endYear === startYear && endMonth > startMonth);
  }

  getHourNames(): string[] {
    return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  }

  getMinuteNames(): string[] {
    return Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  }

  createDatetime(year: number, month: number, date: number, hour: number, minute: number): DateTime {
    const zone = this._useUtc ? 'utc' : Settings.defaultZone;
    return DateTime.fromObject(
      { year, month: month + 1, day: date, hour, minute },
      { zone, locale: this.locale }
    );
  }

  override getDayOfWeek(date: DateTime): number {
    return date.weekday % 7; // Convert Luxon's 1-7 (Mon-Sun) to 0-6 (Sun-Sat)
  }

  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return Info.months(style, { locale: this.locale });
  }

  override getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return Info.weekdays(style, { locale: this.locale });
  }

  override getYearName(date: DateTime): string {
    return date.toFormat('yyyy');
  }

  override getFirstDayOfWeek(): number {
    return this._firstDayOfWeek;
  }

  override getNumDaysInMonth(date: DateTime): number {
    return date.daysInMonth!;
  }

  override clone(date: DateTime): DateTime {
    return DateTime.fromMillis(date.toMillis(), { zone: date.zone, locale: this.locale });
  }

  override createDate(year: number, month: number, date: number): DateTime;
  override createDate(year: number, month: number, date: number, hour: number, minute: number): DateTime;
  override createDate(year: number, month: number, date: number, hour?: number, minute?: number): DateTime {
    const zone = this._useUtc ? 'utc' : Settings.defaultZone;
    return DateTime.fromObject(
      { year, month: month + 1, day: date, hour: hour ?? 0, minute: minute ?? 0 },
      { zone, locale: this.locale }
    );
  }

  override today(): DateTime {
    const zone = this._useUtc ? 'utc' : Settings.defaultZone;
    return DateTime.now().setZone(zone);
  }

  override parse(value: string | DateTime | null | undefined, parseFormat: string | string[]): DateTime | null {
    if (!value) return null;

    const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];

    // If already a DateTime, return it
    if (value instanceof DateTime) {
      return value.isValid ? value : null;
    }

    // Convert to string if needed
    const valueStr = String(value);

    for (const format of formats) {
      const parsed = DateTime.fromFormat(valueStr, format, {
        zone: this._useUtc ? 'utc' : Settings.defaultZone,
        locale: this.locale
      });
      if (parsed.isValid) return parsed;
    }

    // Try ISO format as fallback
    const isoDate = DateTime.fromISO(valueStr, {
      zone: this._useUtc ? 'utc' : Settings.defaultZone,
      locale: this.locale
    });
    return isoDate.isValid ? isoDate : null;
  }

  override format(date: DateTime, displayFormat: string): string {
    if (!this.isValid(date)) return '';
    return date.toFormat(displayFormat);
  }

  override addCalendarYears(date: DateTime, years: number): DateTime {
    return date.plus({ years });
  }

  override addCalendarMonths(date: DateTime, months: number): DateTime {
    return date.plus({ months });
  }

  override addCalendarDays(date: DateTime, days: number): DateTime {
    return date.plus({ days });
  }

  addCalendarHours(date: DateTime, hours: number): DateTime {
    return date.plus({ hours });
  }

  addCalendarMinutes(date: DateTime, minutes: number): DateTime {
    return date.plus({ minutes });
  }

  override toIso8601(date: DateTime): string {
    return date.toISO()!;
  }

  override deserialize(value: string | number | Date | DateTime | null | undefined): DateTime | null {
    if (!value) return null;
    if (value instanceof DateTime) return value;
    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value, {
        zone: this._useUtc ? 'utc' : Settings.defaultZone,
        locale: this.locale
      });
      return parsed.isValid ? parsed : null;
    }
    if (value instanceof Date) {
      return DateTime.fromJSDate(value, {
        zone: this._useUtc ? 'utc' : Settings.defaultZone
      });
    }
    if (typeof value === 'number') {
      return DateTime.fromMillis(value, {
        zone: this._useUtc ? 'utc' : Settings.defaultZone
      });
    }
    return null;
  }

  override isDateInstance(obj: unknown): boolean {
    return obj instanceof DateTime;
  }

  override isValid(date: DateTime): boolean {
    return date?.isValid ?? false;
  }

  override invalid(): DateTime {
    return DateTime.invalid('Invalid date');
  }

  override clampDate(date: DateTime, min?: DateTime | null, max?: DateTime | null): DateTime {
    if (min && date < min) return min;
    if (max && date > max) return max;
    return date;
  }
}

export function provideLuxonDatetimeAdapter(formats?: MtxDatetimeFormats) {
  return [
    { provide: DatetimeAdapter, useClass: MtxLuxonDatetimeAdapter },
    { provide: MTX_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
    ...(formats ? [{ provide: MTX_DATETIME_FORMATS, useValue: formats }] : []),
  ];
}
